import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Star, Share2, Clock, Send } from "lucide-react";
import api from "../lib/api";
import Loader from "./loader";

export default function DoctorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  // Review form state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);

  const maskReviewerName = (name) => {
    const safe = String(name || 'Anonymous').trim();
    if (!safe) return 'Anonymous';

    const words = safe.split(/\s+/).filter(Boolean);
    return words
      .map((word) => {
        if (word.length <= 1) return '*';
        if (word.length === 2) return `${word[0]}*`;
        return `${word[0]}${'*'.repeat(Math.max(1, word.length - 2))}${word[word.length - 1]}`;
      })
      .join(' ');
  };

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/doctor/${id}`);
        setDoctor(res.data);
      } catch (err) {
        console.error("Failed to fetch doctor:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctor();
  }, [id]);

  const availabilitySummary = useMemo(() => {
    const dayOrder = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    const dayLabel = {
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thu",
      friday: "Fri",
      saturday: "Sat",
      sunday: "Sun",
    };

    const items = dayOrder
      .map((day) => {
        const ranges = Array.isArray(doctor?.availability?.[day])
          ? doctor.availability[day].filter(Boolean)
          : [];
        if (!ranges.length) return null;
        return `${dayLabel[day]} ${ranges.join(", ")}`;
      })
      .filter(Boolean);

    return items.length ? items[0] : "Not provided";
  }, [doctor]);

  const totalCost = useMemo(() => {
    const explicitTotal = Number(doctor?.totalConsultationCost);
    if (Number.isFinite(explicitTotal) && explicitTotal > 0) {
      return explicitTotal;
    }

    const remote = Number(doctor?.consultationFeeRemote);
    const inPerson = Number(doctor?.consultationFeeInPerson);
    if (Number.isFinite(remote) && Number.isFinite(inPerson)) {
      return remote + inPerson;
    }

    return null;
  }, [doctor]);

  const qualificationsText = useMemo(() => {
    if (Array.isArray(doctor?.qualifications) && doctor.qualifications.length) {
      return doctor.qualifications.join(", ");
    }

    if (Array.isArray(doctor?.education) && doctor.education.length) {
      return doctor.education.join(", ");
    }

    return "Not provided";
  }, [doctor]);

  const reviews = Array.isArray(doctor?.comments) ? doctor.comments : [];

  const consultationPricingGuide = [
    { key: 'chat', label: 'Messaging / Chat', range: 'US$2 to $5', recommended: 3 },
    { key: 'audio', label: 'Audio Consultation', range: 'US$4 to $8', recommended: 5 },
    { key: 'video', label: 'Video Consultation', range: 'US$6 to $12', recommended: 8 },
    { key: 'follow-up-chat', label: 'Follow-up Messaging', range: 'US$1 to $3', recommended: 2 },
    { key: 'follow-up-audio', label: 'Follow-up Audio', range: 'US$2 to $5', recommended: 3 },
    { key: 'follow-up-video', label: 'Follow-up Video', range: 'US$3 to $7', recommended: 5 },
  ];

  if (loading) return <Loader/>;
  if (!doctor) return <p className="text-center mt-5">Doctor not found.</p>;

  return (
    <div style={{ maxWidth: 560, background: "var(--BG)", margin: "0 auto", minHeight: "100vh", paddingBottom: 90 }}>
      <style>{`
        .profile-container { padding: 12px 16px; }
        .profile-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0; padding: 12px 16px; }
        .header-title { flex: 1; text-align: center; font-size: 16px; font-weight: 600; color: #1a1a1a; }
        .back-btn { background: transparent; border: none; color: var(--on-bg); width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 18px; }
        .back-btn:hover { background: rgba(0,0,0,0.1); }
        .share-btn { background: transparent; border: none; color: var(--on-bg); cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; }
        .profile-card { background: #f9f9f9; border-radius: 12px; padding: 20px 16px; margin: 16px; text-align: center; }
        .profile-avatar { width: 80px; height: 80px; border-radius: 50%; object-fit: cover; margin: 0 auto 12px; display: block; border: 2px solid #eee; }
        .profile-name { font-size: 17px; font-weight: 600; color: #1a1a1a; margin-bottom: 4px; }
        .profile-specialty { font-size: 12px; color: #666; margin-bottom: 2px; font-weight: 500; }
        .profile-credentials { font-size: 11px; color: #999; margin-bottom: 16px; }
        .metrics-row { display: flex; gap: 16px; margin-bottom: 16px; padding: 0; justify-content: space-around; }
        .metric-box { flex: 1; text-align: center; }
        .metric-label { font-size: 10px; color: #999; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 500; }
        .metric-value { font-size: 15px; font-weight: 700; color: #1a1a1a; display: flex; align-items: center; justify-content: center; gap: 4px; }
        .location-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; font-weight: 500; }
        .location-info { font-size: 13px; color: #333; text-align: center; padding-top: 12px; border-top: 1px solid #eee; }
        .location-info strong { display: block; font-weight: 600; color: #1a1a1a; }
        .tabs { display: flex; gap: 0; border-bottom: 1px solid #e5e5e5; margin: 16px; }
        .tab { flex: 1; padding: 12px 0; text-align: center; font-size: 13px; font-weight: 500; color: #999; cursor: pointer; border-bottom: 2px solid transparent; transition: all 0.2s; background: none; }
        .tab.active { color: #1a1a1a; border-bottom-color: var(--primary); font-weight: 600; }
        .glance-section { padding: 0 16px 20px; }
        .glance-item-full { margin-bottom: 14px; display: flex; align-items: flex-start; gap: 10px; }
        .glance-item-full .icon { width: 20px; height: 20px; color: var(--primary); flex-shrink: 0; margin-top: 2px; }
        .glance-item-full .content { flex: 1; }
        .glance-item-full .label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 2px; font-weight: 500; }
        .glance-item-full .value { font-size: 13px; font-weight: 600; color: #1a1a1a; }
        .glance-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
        .glance-grid-item { background: #f9f7ff; padding: 12px; border-radius: 8px; }
        .glance-grid-item .label { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; font-weight: 500; }
        .glance-grid-item .value { font-size: 13px; font-weight: 600; color: #1a1a1a; }
        .section-title { font-size: 14px; font-weight: 600; margin-bottom: 12px; margin-top: 0; color: #1a1a1a; }
        .about-text { font-size: 13px; line-height: 1.6; color: #666; }
        .button-group { position: fixed; left: 0; right: 0; bottom: 0; display: flex; gap: 12px; padding: 12px 16px; background: var(--BG); z-index: 100; max-width: 560px; margin: 0 auto; border-top: 1px solid #e5e5e5; box-shadow: 0 -2px 8px rgba(0,0,0,0.06); }
        .btn-primary { flex: 1; padding: 12px; border-radius: 8px; background: var(--primary); color: white; border: none; font-weight: 600; cursor: pointer; transition: all 0.2s; font-size: 14px; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-secondary { flex: 1; padding: 12px; border-radius: 8px; background: #f0f0f0; color: #1a1a1a; border: none; font-weight: 600; cursor: pointer; transition: all 0.2s; font-size: 14px; }
        .btn-secondary:hover { background: #e5e5e5; }
      `}</style>

      {/* Header */}
      <div className="profile-header">
        <button className="back-btn" onClick={() => navigate(-1)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <i className="fi fi-br-angle-left"></i>
        </button>
        <div className="header-title">Profile</div>
        <button className="share-btn" onClick={() => alert('Share clicked')}>
          <Share2 size={18} />
        </button>
      </div>

      {/* Profile Card */}
      <div className="profile-card">
        <img src={doctor.avatar || 'https://randomuser.me/api/portraits/men/44.jpg'} alt={doctor.name} className="profile-avatar" />
        <div className="profile-name">{doctor.name}</div>
        <div className="profile-specialty">{doctor.specialty || doctor.speciality || 'Not provided'}</div>
        <div className="profile-credentials">{qualificationsText}</div>

        {/* Metrics */}
        <div className="metrics-row">
          <div className="metric-box">
            <div className="metric-label">Total Experience</div>
            <div className="metric-value">
              {Number.isFinite(Number(doctor.experience)) ? `${Number(doctor.experience)}+ yrs` : 'Not provided'}
            </div>
          </div>
          <div className="metric-box">
            <div className="metric-label">Total Rating</div>
            <div className="metric-value">
              <Star size={14} fill="currentColor" style={{ color: '#fbbf24' }} />
              {Number.isFinite(Number(doctor.averageRating)) ? Number(doctor.averageRating).toFixed(1) : 'Not rated'}
            </div>
          </div>
          <div className="metric-box">
            <div className="metric-label">BMDC Number</div>
            <div className="metric-value">{doctor.licenseNumber || 'Not provided'}</div>
          </div>
        </div>

        {/* Location */}
        <div className="location-info">
          <div className="location-label">Working in</div>
          <strong>{doctor.clinicName || doctor.city || 'Not provided'}</strong>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {['info', 'experiences', 'review'].map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
            style={{ cursor: 'pointer', border: 'none' }}
          >
            {tab === 'info' ? 'Info' : tab === 'experiences' ? 'Experiences' : 'Review'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="glance-section">
          <div className="section-title">At a Glance</div>
          
          {/* Instant Consultation Time */}
          <div className="glance-item-full">
            <div className="icon">
              <Clock size={18} />
            </div>
            <div className="content">
              <div className="label">Instant Consultation Time</div>
              <div className="value">{availabilitySummary}</div>
            </div>
          </div>

          {/* Two column grid */}
          <div className="glance-grid">
    
        
          </div>

          <div className="glance-grid">
            <div className="glance-grid-item">
              <div className="label">Patient attended</div>
              <div className="value">
                {Number.isFinite(Number(doctor.patientsAttended)) ? Number(doctor.patientsAttended) : 'Not available'}
              </div>
            </div>
            <div className="glance-grid-item">
              <div className="label">Joined </div>
              <div className="value">
                {doctor.joinDate
                  ? new Date(doctor.joinDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                  : 'Not available'}
              </div>
            </div>
          </div>

          <div className="glance-grid">
            <div className="glance-grid-item">
              <div className="label">Doctor code</div>
              <div className="value">{doctor.doctorCode || 'Not provided'}</div>
            </div>
          
          </div>

         <div style={{ marginBottom: 20 }}>
  <div className="section-title">Consultation Pricing</div>
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: 6,
    }}
  >
    {consultationPricingGuide.map((item) => (
      <div
        key={item.key}
        style={{
          background: '#fff',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          borderRadius: 8,
          padding: '6px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <div style={{ fontSize: 10, fontWeight: 700, color: '#475569' }}>
          {item.label}
        </div>
        <div style={{ fontSize: 11, color: '#0f172a' }}>{item.range}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)' }}>
          From ${item.recommended}
        </div>
      </div>
    ))}
  </div>
</div>

          <div style={{ marginBottom: 20 }}>
            <div className="section-title">At a Glance Note</div>
            <div className="about-text">{doctor.atAGlance || 'Not provided'}</div>
          </div>

          {/* About Doctor */}
          <div style={{ marginTop: 20 }}>
            <div className="section-title">About Doctor</div>
            <div className="about-text">
              {doctor.description || 'Not provided'}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'experiences' && (
        <div style={{ padding: '16px' }}>
          <div className="section-title">Qualifications & Experience</div>
          <div className="about-text">
            <p>
              {Number.isFinite(Number(doctor.experience))
                ? `${Number(doctor.experience)}+ years of experience in ${doctor.specialty || 'the listed specialty'}`
                : 'Experience details not provided yet.'}
            </p>
            <p style={{ marginTop: 12 }}>
              Specializations: {Array.isArray(doctor.skills) && doctor.skills.length ? doctor.skills.join(', ') : 'Not provided'}
            </p>
            <p style={{ marginTop: 12 }}>
              Languages: {Array.isArray(doctor.languagesSpoken) && doctor.languagesSpoken.length ? doctor.languagesSpoken.join(', ') : 'Not provided'}
            </p>
            <p style={{ marginTop: 12 }}>
              Education: {Array.isArray(doctor.education) && doctor.education.length ? doctor.education.join(', ') : 'Not provided'}
            </p>
          </div>
        </div>
      )}

      {activeTab === 'review' && (
        <div style={{ padding: '16px' }}>
          <div className="section-title">Patient Reviews</div>
          {reviews.length === 0 && (
            <div className="about-text" style={{ marginBottom: 16 }}>
              No reviews yet.
            </div>
          )}
          {reviews.map((c, idx) => (
            <div key={idx} style={{ marginBottom: 16, padding: 12, background: '#f9f7ff', borderRadius: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: '#1a1a1a' }}>
                {maskReviewerName(c.user)}
              </div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
                {new Date(c.createdAt || c.date).toLocaleDateString()}
              </div>
              <div className="about-text">{c.comment}</div>
            </div>
          ))}

          {/* Submit review */}
          {reviewDone ? (
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 12, padding: '12px 14px', marginTop: 20, fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>
              Thank you for your review!
            </div>
          ) : (
            <div style={{ marginTop: 24, padding: 16, background: '#f9f7ff', borderRadius: 14, border: '1px solid rgba(127,0,255,0.08)' }}>
              <div className="section-title" style={{ marginBottom: 12 }}>Leave a Review</div>

              {/* Star picker */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <Star
                      size={28}
                      fill={star <= reviewRating ? '#fbbf24' : 'none'}
                      color={star <= reviewRating ? '#fbbf24' : '#d1d5db'}
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>

              <textarea
                rows={3}
                placeholder="Share your experience with this doctor..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                style={{ width: '100%', border: '1px solid #ddd', borderRadius: 10, padding: '10px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }}
              />

              <button
                type="button"
                disabled={reviewSubmitting || reviewRating === 0}
                onClick={async () => {
                  if (!reviewComment.trim() || reviewRating === 0) return;
                  setReviewSubmitting(true);
                  try {
                    const storedUser = localStorage.getItem('user');
                    const userName = storedUser ? (JSON.parse(storedUser)?.fullName || JSON.parse(storedUser)?.name || 'Anonymous') : 'Anonymous';

                    await Promise.all([
                      api.post(`/api/doctor/${id}/comment`, { user: userName, comment: reviewComment }),
                      api.post(`/api/doctor/${id}/rate`, { rating: reviewRating }),
                    ]);

                    const refreshed = await api.get(`/api/doctor/${id}`);
                    setDoctor(refreshed.data);
                    setReviewDone(true);
                  } catch (err) {
                    console.error('Review submit failed:', err);
                  } finally {
                    setReviewSubmitting(false);
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
                  borderRadius: 10, border: 'none', background: reviewRating === 0 ? '#e5e7eb' : 'var(--primary)',
                  color: reviewRating === 0 ? '#9ca3af' : '#fff', fontWeight: 600, fontSize: 13,
                  cursor: reviewRating === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <Send size={14} /> {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Floating Booking Buttons */}
      <div className="button-group">
        <button
          className="btn-secondary"
          onClick={() => navigate('/booking-screen', { state: { ...doctor, bookingType: 'online' } })}
        >
          Online Book
        </button>
        <button
          className="btn-primary"
          onClick={() => navigate('/booking-clinic', { state: { ...doctor, bookingType: 'in-person' } })}
        >
          Physical Book
        </button>
      </div>
    </div>
  );
}